import React, { useEffect, useRef } from 'react';
import { TMUA_PEARSON_TOKENS, getTmuaPearsonChrome } from '../constants/tmuaPearsonTokens';
import { markWideMath } from './examRender/wideMath';
import { keepPunctuationWithMath } from './examRender/mathPunctuation';
import { displayStyleLoneFormula } from './examRender/html';
import {
  OPTION_CARD_CLASS, optionListGap, optionRadioStyle, optionRowAlignment, optionsHavePictures,
} from './examRender/optionRow';

export default function TmuaPearsonSolutionModal({
  question,
  questionIndex,
  userAnswerLabel,
  prepareExamHtml,
  MathContent,
  PaperImage,
  optionHasText,
  questionStyle,
  optionStyle,
  onClose,
  onExhibitTextClick,
}) {
  const T = TMUA_PEARSON_TOKENS;
  const chrome = getTmuaPearsonChrome('pearsonDefault');
  const rootRef = useRef(null);
  const q = question || {};
  const correctOpt = (q.options || []).find(
    (opt) => String(opt.label).trim().toUpperCase() === String(q.correct_answer || '').trim().toUpperCase()
      || Boolean(opt.is_correct)
  );
  const isUnmarkedEssay = Boolean(
    q.is_essay
    || q.interaction_kind === 'essay_choice_group'
    || (Array.isArray(q.essay_options) && q.essay_options.length && !(q.options || []).length)
  );
  const correctLabel = q.correct_answer || correctOpt?.label || null;
  const unmarkedEssayFallback = q.pearsonWritingItem
    ? 'This Writing Task is not marked here.'
    : 'This essay is not marked here.';
  const explanation = q.explanation
    ? (typeof prepareExamHtml === 'function' ? prepareExamHtml(q.explanation) : q.explanation)
    : (isUnmarkedEssay ? unmarkedEssayFallback : 'No worked solution is available for this question.');
  const stem = typeof prepareExamHtml === 'function'
    ? prepareExamHtml(String(q.question || '').replace(/^Question\s+\d+\s*:\s*/i, ''))
    : (q.question || '');

  useEffect(() => {
    let cancelled = false;
    const typeset = async () => {
      if (!window.MathJax) return;
      if (window.MathJax.startup?.promise) {
        try { await window.MathJax.startup.promise; } catch (e) { /* ignore */ }
      }
      if (cancelled || !rootRef.current || !window.MathJax.typesetPromise) return;
      const nodes = [rootRef.current];
      try {
        if (window.MathJax.typesetClear) window.MathJax.typesetClear(nodes);
        await window.MathJax.typesetPromise(nodes);
        if (!cancelled) {
          nodes.forEach(keepPunctuationWithMath);
          nodes.forEach(markWideMath);
        }
      } catch (e) {
        /* ignore typeset races */
      }
    };
    const t = setTimeout(typeset, 80);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [q.id, questionIndex, explanation, stem]);

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 20,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        pointerEvents: 'auto',
        background: 'transparent',
      }}
    >
      <div
        ref={rootRef}
        style={{
          width: 860,
          maxWidth: '96%',
          height: '100%',
          maxHeight: '100%',
          background: '#FFFFFF',
          fontFamily: 'Tahoma, "Segoe UI", sans-serif',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 10px 28px rgba(0,0,0,0.4)',
          border: `2px solid ${chrome.titleBar}`,
          pointerEvents: 'auto',
          position: 'relative',
        }}
      >
        <div
          style={{
            flex: 'none',
            background: chrome.titleBar,
            color: chrome.chromeFg,
            padding: '7px 10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: 14,
            fontWeight: 700,
          }}
        >
          <span>Solution</span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'transparent',
              border: `1px solid ${chrome.chromeFg}`,
              color: chrome.chromeFg,
              fontSize: 14,
              cursor: 'pointer',
              lineHeight: 1,
              width: 22,
              height: 18,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
            }}
          >
            ×
          </button>
        </div>
        <div style={{ padding: '16px 18px 24px', overflowY: 'auto', color: '#000', fontSize: 14, lineHeight: 1.5, flex: 1, minHeight: 0 }}>
          {typeof questionIndex === 'number' ? (
            <div style={{ fontWeight: 700, marginBottom: 10 }}>Question {questionIndex + 1}</div>
          ) : null}
          {MathContent ? (
            <MathContent
              style={questionStyle}
              className="question-text mathjax-process tex2jax_process exhibit-clickable-html"
              html={stem}
              onClick={onExhibitTextClick}
            />
          ) : (
            <div className="mathjax-process tex2jax_process exhibit-clickable-html" onClick={onExhibitTextClick} dangerouslySetInnerHTML={{ __html: stem }} />
          )}
          {PaperImage ? <PaperImage url={q.image_url} alt="" /> : null}
          {isUnmarkedEssay ? null : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: optionListGap(q.options, 8), margin: '12px 0 20px' }}>
              {(q.options || []).map((option) => {
                const isSelected = userAnswerLabel === option.label;
                const isCorrect = String(option.label).trim().toUpperCase() === String(correctLabel || '').trim().toUpperCase();
                const hasText = Boolean(optionHasText && optionHasText(option.text));
                return (
                  <label key={option.label} className={optionsHavePictures(q.options) ? OPTION_CARD_CLASS : undefined} style={{ display: 'flex', alignItems: optionRowAlignment(hasText), gap: 10, cursor: 'default' }}>
                    <input type="radio" name={`tmua-sol-${q.id}`} checked={Boolean(isSelected)} disabled readOnly style={optionRadioStyle(hasText)} />
                    <span style={{ flex: 1, minWidth: 0, fontWeight: isCorrect ? 700 : 400 }}>
                      {hasText && MathContent ? (
                        <MathContent
                          className="mathjax-process tex2jax_process exhibit-clickable-html"
                          style={optionStyle}
                          html={displayStyleLoneFormula(typeof prepareExamHtml === 'function' ? prepareExamHtml(option.text) : option.text)}
                          onClick={onExhibitTextClick}
                        />
                      ) : (
                        <span className="mathjax-process tex2jax_process exhibit-clickable-html" onClick={onExhibitTextClick}>{option.text}</span>
                      )}
                      {PaperImage ? <PaperImage url={option.image_url} alt="" maxHeight="260px" /> : null}
                    </span>
                  </label>
                );
              })}
            </div>
          )}
          <div style={{ borderTop: `1px solid ${T.toolbarBg}`, paddingTop: 14, color: '#14161F' }}>
            {isUnmarkedEssay ? null : (correctLabel ? (
              <p style={{ margin: '0 0 12px', color: '#000' }}>
                The correct answer is <strong>{correctLabel}</strong>.
              </p>
            ) : isUnmarkedEssay ? null : (
              <p style={{ margin: '0 0 12px' }}>A marked correct option is not available for this question.</p>
            ))}
            {MathContent ? (
              <MathContent
                className="question-text mathjax-process tex2jax_process"
                html={explanation}
              />
            ) : (
              <div
                className="mathjax-process tex2jax_process"
                dangerouslySetInnerHTML={{ __html: explanation }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
